import { useEffect, useId, useRef, useMemo, useSyncExternalStore, useCallback } from 'react';
import { ChainOfThoughtPrimitive, useAuiState } from '@assistant-ui/react';
import { AnimatePresence } from 'motion/react';
import { useThoughts } from '../../contexts/ThoughtsContext';
import {
  runStateStore,
  type LiveIterationSnapshot,
} from '../../hooks/useAsyncInvokeAdapter';
import { useRunElapsedSeconds, useThinkingHint, ThoughtHeaderContent } from './cot-header';
import { AnimatedListItem } from '../ui/AnimatedListItem';
import { playCue } from '../../utils/audio-theme';
import { eventDebug } from '../../utils/event-debug';
import { deriveTimelineEntries } from './cot-derive';
import { TimelineEntryRow } from './cot-timeline';
import { useConversationTasks, ConversationTaskCard } from './conversation-run-task-list';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ThinkingStep {
  index: number;
  reasoning: readonly Record<string, unknown>[];
  tools: readonly Record<string, unknown>[];
}

// (Timeline entry types + row components moved to ./cot-timeline so the
// delegated-task card can share them.)

// ─── Step grouping (preserved for ThoughtsContext / side panel) ─────────────

/**
 * Groups chain-of-thought parts into "steps". A new step starts when a
 * reasoning part appears after one or more tool-call parts.
 */
export function groupPartsIntoSteps(
  parts: readonly { type: string; [key: string]: unknown }[],
): ThinkingStep[] {
  const steps: ThinkingStep[] = [];
  let current: { reasoning: Record<string, unknown>[]; tools: Record<string, unknown>[] } = {
    reasoning: [],
    tools: [],
  };

  for (const part of parts) {
    if (part.type === 'reasoning') {
      // Start a new step if we already collected tool calls in the current one
      if (current.tools.length > 0) {
        steps.push({ index: steps.length, reasoning: current.reasoning, tools: current.tools });
        current = { reasoning: [], tools: [] };
      }
      current.reasoning.push(part);
    } else if (part.type === 'tool-call') {
      current.tools.push(part);
    }
  }

  // Push the final step (even if empty reasoning — it means tools are running)
  if (current.reasoning.length > 0 || current.tools.length > 0) {
    steps.push({ index: steps.length, reasoning: current.reasoning, tools: current.tools });
  }

  return steps;
}

/**
 * Merges live tool calls from the adapter's iteration store into the steps
 * derived from assistant-ui parts. Adds tool-call parts that assistant-ui
 * hasn't propagated yet (due to reactive batching during streaming).
 */
function mergeWithLiveToolCalls(
  steps: ThinkingStep[],
  liveSnapshot: LiveIterationSnapshot,
): ThinkingStep[] {
  // Collect tool call IDs already present in steps
  const existingIds = new Set<string>();
  for (const step of steps) {
    for (const tc of step.tools) {
      if (tc.toolCallId) existingIds.add(String(tc.toolCallId));
    }
  }

  // Collect missing tool calls from live iterations
  const missingTools: Record<string, unknown>[] = [];
  for (const iter of liveSnapshot.iterations) {
    for (const tc of iter.toolCalls) {
      // __ask_user renders as an inline card in the message column, never as a
      // CoT step — keep it out of the step grouping / side panel too.
      if (tc.toolName === '__ask_user') continue;
      if (!existingIds.has(tc.toolCallId)) {
        missingTools.push({
          type: 'tool-call',
          toolCallId: tc.toolCallId,
          toolName: tc.toolName,
          toolDisplayName: tc.toolDisplayName,
          label: tc.label,
          args: {},
          argsText: '{}',
          result: tc.hasResult ? '...' : undefined,
          isError: false,
        });
      }
    }
  }

  if (missingTools.length === 0) return steps;

  // Add missing tool calls to the last step, or create a new step
  if (steps.length > 0) {
    const last = steps[steps.length - 1];
    return [
      ...steps.slice(0, -1),
      { ...last, tools: [...last.tools, ...missingTools] },
    ];
  }
  return [{ index: 0, reasoning: [], tools: missingTools }];
}


// ─── Constants ─────────────────────────────────────────────────────────────

const DEFAULT_MAX_ITEMS = 5;

// ─── Main component ─────────────────────────────────────────────────────────

interface AgentChainOfThoughtProps {
  /** Max visible items in the rolling timeline. Default: 5. */
  maxItems?: number;
  /** Max visible items per sub-agent's mini-timeline. Default: 3. */
  subAgentMaxItems?: number;
}

/**
 * Playful gerunds shown in place of "Thinking..." while a run is active.
 * One is picked at random per run and locked for the rest of the message
 * lifecycle (the trailing ellipsis is appended in the component).
 */
const COT_LABELS: readonly string[] = [
  // Cooking
  'Baking', 'Brewing', 'Caramelizing', 'Cooking', 'Fermenting', 'Flambeing',
  'Frosting', 'Garnishing', 'Julienning', 'Kneading', 'Leavening',
  'Marinating', 'Proofing', 'Sauteing', 'Seasoning', 'Simmering', 'Stewing',
  'Tempering', 'Whisking', 'Zesting',
  // Movement
  'Cascading', 'Frolicking', 'Gallivanting', 'Galloping', 'Moonwalking',
  'Scampering', 'Scurrying', 'Shimmying', 'Skedaddling', 'Waddling',
  // Cogitation
  'Cerebrating', 'Cogitating', 'Contemplating', 'Deliberating', 'Musing',
  'Philosophising', 'Pondering', 'Ruminating',
  // Physical chemistry
  'Crystallizing', 'Ionizing', 'Nebulizing', 'Nucleating', 'Osmosing',
  'Photosynthesizing', 'Precipitating', 'Sublimating',
  // Dance
  'Grooving', 'Harmonizing', 'Jiving', 'Sock-hopping',
  // Lab bench
  'Pipetting', 'Vortexing', 'Centrifuging', 'Incubating', 'Culturing',
  'Sequencing', 'Amplifying', 'Hybridizing', 'Annealing', 'Denaturing',
  'Eluting', 'Lyophilizing', 'Homogenizing', 'Sonifying', 'Screening',
  'Optimizing', 'Formulating', 'Encapsulating', 'Assaying',
  // Cell biology
  'Fusing', 'Engulfing', 'Remodeling', 'Swarming', 'Pulsing', 'Oscillating',
  'Branching',
  // Molecular biology
  'Transcribing', 'Translating', 'Replicating', 'Folding', 'Assembling',
  'Signaling', 'Expressing', 'Methylating', 'Metabolizing',
  // DNA/RNA chemistry
  'Splicing', 'Polymerizing', 'Catalyzing', 'Cleaving', 'Mutagenizing',
  'Ubiquitinating',
  // Aerospace
  'Igniting', 'Thrusting', 'Propelling', 'Boosting', 'Launching', 'Orbiting',
  'Maneuvering', 'Reentering',
  // Flight
  'Spiraling', 'Gliding', 'Slingshotting', 'Swooping', 'Hovering',
  // Navigation
  'Navigating', 'Commanding', 'Resonating',
  // Astrophysics
  'Pulsating', 'Gravitating', 'Redshifting', 'Radiating', 'Warping',
];

/**
 * Chain-of-thought component with rolling timeline.
 *
 * While running: shows the last N steps with animated transitions.
 * After completion: collapses to "Thought for Xs" button that opens
 * the Thoughts tab in the side panel.
 */
export function AgentChainOfThought({ maxItems = DEFAULT_MAX_ITEMS, subAgentMaxItems = 3 }: AgentChainOfThoughtProps = {}) {
  const status = useAuiState((s) => s.chainOfThought.status);
  const parts = useAuiState((s) => s.chainOfThought.parts);
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const messageStatus = useAuiState((s) => s.message.status);

  const isRunning = status.type === 'running';

  // Per-message runId from metadata. Plumbed by the adapter on the first
  // post-invokeAsync yield (and again on terminal). Used to address this
  // message's slot in the per-runId state store — concurrent runs cannot
  // pollute each other.
  const perMsgRunId = (messageMetadata?.custom as Record<string, unknown> | undefined)?.runId as string | undefined;

  // Subscribe to the run-state store for THIS message's runId. Real-time
  // tool call visibility bypasses assistant-ui's reactive batching.
  const subscribeRun = useCallback(
    (listener: () => void) => runStateStore.subscribe(perMsgRunId, listener),
    [perMsgRunId],
  );
  const getRunSnapshot = useCallback(
    () => runStateStore.getSnapshot(perMsgRunId),
    [perMsgRunId],
  );
  const runSnapshot = useSyncExternalStore(subscribeRun, getRunSnapshot);

  // Only use live data for the actively running message
  const activeLiveSnapshot = useMemo(
    () => (isRunning ? runSnapshot.live : { iterations: [], version: 0 }),
    [isRunning, runSnapshot.live],
  );

  // Terminal status — gate strictly on the MESSAGE status. The
  // chainOfThought primitive's own status flips to 'complete' on its own
  // whenever no reasoning/tool-call parts are being emitted (e.g. mid-run,
  // between iterations), so it is NOT a reliable "the run is done" signal.
  const isTerminalStatus =
    messageStatus?.type === 'incomplete'
    || messageStatus?.type === 'complete';

  // Errors come straight off the message status (the terminal yield carries
  // status.type='incomplete', reason='error'). Restored failed runs preserve
  // this on reload via assistant-ui's message status.
  const isRunError =
    messageStatus?.type === 'incomplete' &&
    (messageStatus as { reason?: string }).reason === 'error';

  // Collapse the thinking timeline to "Thought for Xs" when the agent emits its
  // FIRST final-answer token (hasTextContent) — NOT on final_answer_start.
  // There's a visible gap between the start event and the first delta;
  // collapsing on start shows an empty answer area during that gap. The
  // collapse and the answer text now appear together (the text part also
  // populates from the first delta). Completion is the backstop — it covers
  // runs that never stream a final answer (cancelled, legacy, restored).
  const reachedFinalAnswer =
    runSnapshot.progress.hasTextContent ||
    runSnapshot.progress.isRunComplete ||
    isTerminalStatus;

  // Freeze once collapsed so a subsequent runStateStore slot deletion (live
  // runs after scheduleDelete) or an empty slot (restored conversations)
  // can't pop the timeline back open.
  const frozenCollapsedRef = useRef(false);
  if (!frozenCollapsedRef.current && reachedFinalAnswer) {
    frozenCollapsedRef.current = true;
  }

  const isFinalAnswerPhase = frozenCollapsedRef.current;

  const showThinkingView = !isRunError && !isFinalAnswerPhase;

  // "Still working" gates the live elapsed timer and the long-thinking hint.
  // Gate on the MESSAGE status, not just chainOfThought.status: the latter
  // flips to 'complete' on its own during silent stretches (nothing streaming),
  // which is precisely when a long reasoning block needs the ticking timer and
  // the "Thinking more..." reassurance.
  const isMessageRunning = messageStatus?.type === 'running';
  const isStillWorking = !isFinalAnswerPhase && (isRunning || isMessageRunning);

  const stepLabelRef = useRef<string | null>(null);
  if (!stepLabelRef.current) {
    stepLabelRef.current = `${COT_LABELS[Math.floor(Math.random() * COT_LABELS.length)]}...`;
  }
  const stepLabel = stepLabelRef.current;

  // Step grouping — preserved for ThoughtsContext / side panel
  const baseSteps = useMemo(() => groupPartsIntoSteps(parts), [parts]);
  const steps = useMemo(
    () => (isRunning ? mergeWithLiveToolCalls(baseSteps, activeLiveSnapshot) : baseSteps),
    [baseSteps, activeLiveSnapshot, isRunning],
  );

  // Timeline entries — rolling window for inline display
  const allEntries = useMemo(
    () => deriveTimelineEntries(parts, activeLiveSnapshot, subAgentMaxItems),
    [parts, activeLiveSnapshot, subAgentMaxItems],
  );
  const visibleEntries = allEntries.slice(-maxItems);

  // Diagnostic: surface what the CoT renderer actually has to draw with. A
  // running message with a runId but zero timeline entries + an empty live
  // snapshot is the visible face of the "no CoT events" bug — it means no
  // step events reached this message's runStateStore slot or parts array.
  useEffect(() => {
    if (!eventDebug.isEnabled()) return;
    // Summarize what the renderer actually has to draw with. The
    // entry/window breakdown is the non-replayable cross-check for the
    // "stuck bottom, changing top" bug: a completed `subagent-group` that
    // never leaves `visibleEntries` while only the rows above it change is
    // the signature of the rolling-window pinning issue.
    const summarizeEntries = (es: typeof allEntries) =>
      es.map((e) =>
        e.kind === 'subagent-group'
          ? `subagent-group[${e.children.map((c) => c.status).join(',')}]`
          : e.kind === 'tool-call'
            ? `tool-call:${e.detail ?? e.toolDisplayName ?? e.toolName}`
            : e.kind,
      );
    eventDebug.log('cot', 'render', {
      runId: perMsgRunId,
      isRunning,
      isFinalAnswerPhase,
      messageStatus: messageStatus?.type,
      partsLen: parts.length,
      partTypes: parts.reduce<Record<string, number>>((acc, p) => {
        acc[p.type] = (acc[p.type] ?? 0) + 1;
        return acc;
      }, {}),
      liveVersion: activeLiveSnapshot.version,
      liveIterations: activeLiveSnapshot.iterations.length,
      liveToolCalls: activeLiveSnapshot.iterations.reduce((n, it) => n + it.toolCalls.length, 0),
      timelineEntries: allEntries.length,
      allEntries: summarizeEntries(allEntries),
      visibleEntries: summarizeEntries(visibleEntries),
    });
  }, [
    perMsgRunId,
    isRunning,
    isFinalAnswerPhase,
    messageStatus?.type,
    parts.length,
    activeLiveSnapshot.version,
    activeLiveSnapshot.iterations.length,
    allEntries.length,
  ]);

  // Audio: ding once per new timeline entry. Tracks played ids in a ref so
  // re-renders don't double-fire and an already-populated message (reopening
  // a finished thread) absorbs existing ids silently on first run instead
  // of replaying every step. Cue depends on the entry kind: narration is a
  // soft tick, tool/subagent calls use a firmer tick to read as an action.
  const playedEntryIdsRef = useRef<Set<string>>(new Set());
  const playedInitializedRef = useRef(false);
  useEffect(() => {
    const played = playedEntryIdsRef.current;
    if (!playedInitializedRef.current) {
      for (const entry of allEntries) played.add(entry.id);
      playedInitializedRef.current = true;
      return;
    }
    for (const entry of allEntries) {
      if (played.has(entry.id)) continue;
      played.add(entry.id);
      if (entry.kind === 'narration') {
        playCue('cot.narration');
      } else if (entry.kind === 'tool-call' || entry.kind === 'subagent-group') {
        playCue('cot.tool');
      }
      // 'waiting' is a placeholder spinner row — silent.
    }
  }, [allEntries]);

  // Diagnostic logging — only when tool calls are present to reduce noise
  if (process.env.NODE_ENV === 'development') {
    const liveToolCount = activeLiveSnapshot.iterations.reduce((n, it) => n + it.toolCalls.length, 0);
    if (liveToolCount > 0) {
      const stepToolCount = steps.reduce((n, s) => n + s.tools.length, 0);
      console.log('[CoT]', { runId: perMsgRunId, liveVersion: runSnapshot.live.version, liveToolCount, stepToolCount, isRunning, timelineEntries: allEntries.length });
    }
  }

  // Elapsed timer — ticks live while working, then freezes to the run's
  // authoritative duration. Shared with the single-turn fallback header.
  const displayElapsed = useRunElapsedSeconds(isStillWorking);

  // Long silent reasoning block (no events for a while, e.g. high reasoning
  // effort thinking before its first tool call) — append escalating
  // reassurance to the header metadata ("(18s · still thinking)" →
  // "(31s · thinking more)" → ...) so the run reads as healthy, not stalled.
  // The playful gerund label stays put.
  const thinkingHint = useThinkingHint(isStillWorking, runSnapshot.live.version);

  // Derive error from message status
  const errorText = messageStatus?.type === 'incomplete' && (messageStatus as { reason?: string }).reason === 'error'
    ? (messageStatus as Record<string, unknown>).error as string | undefined
    : undefined;

  // Sync thoughts to context for the side panel
  const thoughtId = useId();
  const { registerThoughts, unregisterThoughts, selectThought, activeThoughtId, thoughtsActive, panelMounted } = useThoughts();

  useEffect(() => {
    registerThoughts(thoughtId, { steps, elapsed: displayElapsed, isRunning, runId: perMsgRunId, error: errorText });
  }, [steps, displayElapsed, isRunning, thoughtId, registerThoughts, perMsgRunId, errorText]);

  useEffect(() => {
    return () => unregisterThoughts(thoughtId);
  }, [thoughtId, unregisterThoughts]);

  const isSelected = activeThoughtId === thoughtId && thoughtsActive;

  // Reserve a fixed height for the rolling timeline window — maxItems rows at
  // ~1.75rem each (real row height ≈1.5rem + slack). Keeping it fixed means new
  // CoT entries animate into reserved space instead of pushing the task list
  // below down. Sized to the real row height, not the old worst-case 4rem/row
  // that left a large empty gap above the tasks.
  const timelineHeight = `${maxItems * 1.75}rem`;

  // Live task list for this run, rendered below the reserved timeline area.
  const runTasks = useConversationTasks(perMsgRunId);

  // The header is only a clickable control when a thoughts panel is mounted to
  // receive the selection (the Sandbox). Elsewhere the click is a no-op, so we
  // render it as plain, non-interactive text.
  const headerClassName = `flex w-full items-center gap-2 rounded py-1 text-sm text-muted-foreground ${
    panelMounted ? 'focus-ring cursor-pointer hover:text-foreground/80' : 'cursor-default'
  } ${showThinkingView ? 'font-semibold' : ''} ${isSelected ? 'text-foreground' : ''}`;

  const headerContent = (
    <ThoughtHeaderContent
      working={showThinkingView}
      label={stepLabel}
      elapsedSeconds={displayElapsed}
      hint={thinkingHint}
    />
  );

  return (
    <ChainOfThoughtPrimitive.Root className="mt-0.5 mb-2 overflow-hidden">
      {panelMounted ? (
        <button
          type="button"
          data-testid="cot-thinking-header"
          className={headerClassName}
          onClick={() => selectThought(thoughtId)}
        >
          {headerContent}
        </button>
      ) : (
        <div data-testid="cot-thinking-header" className={headerClassName}>
          {headerContent}
        </div>
      )}
      {showThinkingView && (
        <div
          // ml-6 (24px) aligns the timeline item icons with the header TEXT
          // start: the header is [mark 16px][gap-2 8px][label], so its label
          // begins 24px in — matching that keeps the CoT items on the same
          // left edge as "Whisking…" rather than the header icon.
          className="ml-6 overflow-hidden"
          style={{ minHeight: timelineHeight }}
        >
          <AnimatePresence initial={false}>
            {visibleEntries.map((entry) => (
              <AnimatedListItem key={entry.id}>
                <div className="py-0.5">
                  <TimelineEntryRow entry={entry} />
                </div>
              </AnimatedListItem>
            ))}
          </AnimatePresence>
        </div>
      )}
      {/* Task list for this run, rendered directly below the CoT. Persists
          across the collapse to "Thought for Xs"; renders nothing when the run
          has no tasks. */}
      <ConversationTaskCard tasks={runTasks} className="mt-1" />
    </ChainOfThoughtPrimitive.Root>
  );
}
